import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  LogOut,
  Menu,
  Package,
  Plus,
  Settings,
  Shield,
  Sparkles,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { getAuthUser } from '../utils/auth';
import { api } from '../api/client';

const Layout = ({ children, onLogout, pageTitle, pageSubtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const authUser = getAuthUser();
  const notificationsRef = useRef(null);
  const createMenuRef = useRef(null);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
    setShowCreateMenu(false);
    setShowProfileMenu(false);
    setShowNotifications(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (createMenuRef.current && !createMenuRef.current.contains(event.target)) {
        setShowCreateMenu(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread/count'),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleOpenNotification = async (notification) => {
    try {
      if (!notification.is_read) {
        await api.put(`/notifications/${notification.id}/read`);
      }
      setShowNotifications(false);
      await fetchNotifications();
      if (notification.link) {
        navigate(notification.link);
      }
    } catch (error) {
      console.error('Error opening notification:', error);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await api.put('/notifications/all/read');
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/invoices', label: 'Invoices', icon: FileText },
    { path: '/estimates', label: 'Estimates', icon: FileText },
    { path: '/projects', label: 'Projects', icon: Package },
    { path: '/expenses', label: 'Expenses', icon: Wallet },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/vendors', label: 'Vendors', icon: Users },
    { path: '/reports', label: 'Reports', icon: TrendingUp },
    { path: '/tax-report', label: 'Tax Report', icon: Wallet },
    { path: '/admin-dashboard', label: 'Security', icon: Shield, adminOnly: true },
  ];

  const mobileNavItems = menuItems.filter((item) => ['/dashboard', '/invoices', '/projects', '/reports'].includes(item.path));

  const handleLogout = () => {
    localStorage.removeItem('token');
    if (onLogout) onLogout();
    navigate('/login');
  };

  const userName = authUser?.name || authUser?.email?.split('@')[0] || 'Workspace';
  const userEmail = authUser?.email || 'signed-in user';
  const userInitial = userName.slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen">
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-80 transform flex-col border-r border-black/5 bg-[rgba(247,244,239,0.88)] shadow-[0_18px_44px_rgba(23,34,28,0.08)] backdrop-blur-2xl transition-all duration-300 ${
          sidebarCollapsed ? 'lg:w-24' : 'lg:w-80'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className="flex items-center justify-between px-5 pb-5 pt-6">
          <Link to="/dashboard" className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="flex h-12 w-12 items-center justify-center rounded-[22px] bg-gradient-to-br from-emerald-700 to-emerald-900 text-white shadow-lg">
              <Sparkles size={20} />
            </div>
            {!sidebarCollapsed && (
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">Solaraith</p>
                <h1 className="text-xl font-bold text-slate-950">Accounting</h1>
              </div>
            )}
          </Link>

          {!sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed(true)}
              className="hidden h-11 w-11 items-center justify-center rounded-2xl border border-black/5 bg-white/80 text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white lg:inline-flex"
              title="Collapse sidebar"
            >
              <ChevronLeft size={18} />
            </button>
          )}
        </div>

        <div className="px-4">
          <div className={`rounded-[28px] border border-black/5 bg-white/80 p-4 shadow-sm ${sidebarCollapsed ? 'px-2' : ''}`}>
            {!sidebarCollapsed && (
              <div className="mb-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">Workspace</p>
                <p className="mt-1 text-sm leading-6 text-slate-500">Move between billing, customers, reporting, and operations.</p>
              </div>
            )}

            <nav className="space-y-2">
              {menuItems.filter((item) => !item.adminOnly || authUser?.role === 'admin').map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    className={`group flex items-center rounded-[22px] px-3 py-3 text-sm font-medium transition ${
                      isActive
                        ? 'bg-emerald-700 text-white shadow-lg'
                        : 'text-slate-600 hover:-translate-y-0.5 hover:bg-white hover:text-slate-950'
                    } ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}
                  >
                    <Icon size={18} />
                    {!sidebarCollapsed && <span>{item.label}</span>}
                    {sidebarCollapsed && (
                      <span className="pointer-events-none absolute left-[5.6rem] hidden whitespace-nowrap rounded-2xl border border-black/5 bg-white px-3 py-2 text-xs font-semibold text-slate-800 shadow-xl group-hover:block">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="mt-auto px-4 pb-6 pt-4">
          <div className={`rounded-[28px] border border-black/5 bg-[rgba(255,255,255,0.72)] p-4 shadow-sm ${sidebarCollapsed ? 'px-2' : ''}`}>
            {!sidebarCollapsed ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-[20px] bg-emerald-100 text-emerald-700">
                    {userInitial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{userName}</p>
                    <p className="truncate text-xs text-slate-500">{userEmail}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Link to="/settings" className="btn-secondary flex-1 !py-2.5">
                    <Settings size={16} />
                    Settings
                  </Link>
                  <button type="button" onClick={handleLogout} className="btn-danger flex-1 !py-2.5">
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setSidebarCollapsed(false)}
                className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-white"
                title="Expand sidebar"
              >
                <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </aside>

      <div className={`${sidebarCollapsed ? 'lg:ml-24' : 'lg:ml-80'} min-h-screen transition-all duration-300`}>
        <header className="sticky top-0 z-30 border-b border-black/5 bg-[rgba(247,244,239,0.82)] backdrop-blur-2xl">
          <div className="mx-auto max-w-[1600px] px-4 pb-4 pt-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-black/5 bg-white/80 text-slate-700 shadow-sm lg:hidden"
                  aria-label="Open navigation"
                >
                  <Menu size={18} />
                </button>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">Operations Workspace</p>
                  <h1 className="mt-1 text-2xl font-bold text-slate-950 md:text-3xl">{pageTitle || 'Solaraith Accounting'}</h1>
                  <p className="mt-1 text-sm text-slate-500 md:text-base">{pageSubtitle || 'Manage invoices, projects, reporting, and customer operations in one place.'}</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div ref={notificationsRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowNotifications((s) => !s)}
                    className="inline-flex items-center gap-2 rounded-[22px] border border-black/5 bg-white/80 px-4 py-3 shadow-sm transition hover:-translate-y-0.5 hover:bg-white"
                  >
                    <Bell size={16} className="text-slate-600" />
                    <span className="text-sm font-semibold text-slate-900">{unreadCount} unread</span>
                  </button>
                  {showNotifications && (
                    <div className="absolute right-0 mt-3 w-[min(92vw,24rem)] overflow-hidden rounded-[24px] border border-black/5 bg-white/95 shadow-2xl backdrop-blur-xl">
                      <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Notifications</p>
                          <p className="text-sm text-slate-500">Recent activity and follow-ups</p>
                        </div>
                        <button
                          type="button"
                          onClick={handleMarkAllNotificationsRead}
                          className="inline-flex items-center gap-1 rounded-xl border border-black/5 bg-[#f7f4ef] px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-white"
                        >
                          <CheckCheck size={14} />
                          Mark all read
                        </button>
                      </div>

                      <div className="max-h-96 overflow-y-auto p-2">
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => handleOpenNotification(notification)}
                              className={`block w-full rounded-2xl px-4 py-3 text-left transition ${
                                notification.is_read ? 'hover:bg-[#f7f4ef] opacity-80' : 'bg-emerald-50/70 hover:bg-[#f7f4ef]'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-900">
                                    {notification.title || notification.category || 'Notification'}
                                  </p>
                                  <p className="mt-1 text-sm text-slate-600">{notification.message}</p>
                                  <p className="mt-2 text-xs text-slate-400">
                                    {new Date(notification.created_at).toLocaleString()}
                                  </p>
                                </div>
                                {!notification.is_read && <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-emerald-500" />}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-8 text-center text-sm text-slate-500">
                            No notifications yet.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div ref={createMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCreateMenu((s) => !s)}
                    className="btn-primary"
                  >
                    <Plus size={16} />
                    New
                    <ChevronDown size={14} />
                  </button>
                  {showCreateMenu && (
                    <div className="absolute right-0 mt-3 w-72 overflow-hidden rounded-[24px] border border-black/5 bg-white/95 shadow-2xl backdrop-blur-xl">
                      <div className="border-b border-black/5 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Quick Actions</p>
                        <p className="mt-1 text-sm text-slate-500">Create records or jump into your most-used workspaces.</p>
                      </div>

                      <div className="p-2">
                        <Link to="/invoices/create" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <FileText size={16} className="mt-0.5 text-emerald-700" />
                          <div>
                            <p className="font-semibold text-slate-900">Create Invoice</p>
                            <p className="mt-0.5 text-xs text-slate-500">Bill a customer and send payment details.</p>
                          </div>
                        </Link>
                        <Link to="/estimates/create" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <FileText size={16} className="mt-0.5 text-blue-600" />
                          <div>
                            <p className="font-semibold text-slate-900">Create Estimate</p>
                            <p className="mt-0.5 text-xs text-slate-500">Draft a quote before converting it later.</p>
                          </div>
                        </Link>
                        <Link to="/expenses/create" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <Wallet size={16} className="mt-0.5 text-rose-600" />
                          <div>
                            <p className="font-semibold text-slate-900">Add Expense</p>
                            <p className="mt-0.5 text-xs text-slate-500">Capture a cost, tax amount, and category.</p>
                          </div>
                        </Link>
                      </div>

                      <div className="border-t border-black/5 p-2">
                        <Link to="/customers" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <Users size={16} className="mt-0.5 text-amber-600" />
                          <div>
                            <p className="font-semibold text-slate-900">Customers</p>
                            <p className="mt-0.5 text-xs text-slate-500">Open customer records and follow up faster.</p>
                          </div>
                        </Link>
                        <Link to="/projects" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <Package size={16} className="mt-0.5 text-violet-600" />
                          <div>
                            <p className="font-semibold text-slate-900">Projects</p>
                            <p className="mt-0.5 text-xs text-slate-500">Check delivery progress and linked billing.</p>
                          </div>
                        </Link>
                        <Link to="/reports" className="flex items-start gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                          <TrendingUp size={16} className="mt-0.5 text-emerald-700" />
                          <div>
                            <p className="font-semibold text-slate-900">Reports</p>
                            <p className="mt-0.5 text-xs text-slate-500">Review cash flow, profit, tax, and trends.</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <div ref={profileMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShowProfileMenu((s) => !s)}
                    className="flex items-center gap-3 rounded-[24px] border border-black/5 bg-white/80 px-3 py-2 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-sm font-semibold text-emerald-700">
                      {userInitial}
                    </div>
                    <div className="hidden text-left sm:block">
                      <p className="text-sm font-semibold text-slate-900">{userName}</p>
                      <p className="text-xs text-slate-500">{userEmail}</p>
                    </div>
                    <ChevronDown size={14} className="text-slate-500" />
                  </button>
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-3 w-56 rounded-[24px] border border-black/5 bg-white/95 p-2 shadow-2xl backdrop-blur-xl">
                      <Link to="/profile" className="block rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                        Profile
                      </Link>
                      <Link to="/settings" className="block rounded-2xl px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-[#f7f4ef] hover:text-slate-950">
                        Settings
                      </Link>
                      <button type="button" onClick={handleLogout} className="block w-full rounded-2xl px-4 py-3 text-left text-sm font-medium text-rose-600 transition hover:bg-rose-50">
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-10">
          <div className="app-page-shell">
            <div className="app-surface">{children}</div>
          </div>
        </main>
      </div>

      <nav className="fixed bottom-4 left-1/2 z-40 flex w-[min(92vw,480px)] -translate-x-1/2 items-center justify-between rounded-[28px] border border-black/5 bg-white/88 px-3 py-2 shadow-2xl backdrop-blur-xl lg:hidden">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex min-w-[72px] flex-col items-center gap-1 rounded-[22px] px-3 py-2 text-xs font-medium transition ${
                isActive ? 'bg-emerald-700 text-white shadow-lg' : 'text-slate-600'
              }`}
            >
              <Icon size={18} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
